import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  Row,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody
} from 'reactstrap';

import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';
import { SvgContainer } from '../styles';
import { TransferMultiplesChatsButtonAltIcon } from './styled';
import { GrTransaction } from 'react-icons/gr';

import useAgentsLoggedInQueues from 'hooks/useAgentsLoggedInQueues';
import useQueues from 'hooks/useQueues';
import { verifyRoles } from 'components/Authentication/VerifyRoles';
import { useSocketConversationChatTs } from 'hooks/useSocketConversationChatTs';

const sortOptionsByLabel = (options) => {
  if (!options?.length) return [];
  return [...options].sort((a, b) => {
    if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
    if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
    return 0;
  });
};

const TransferManyButton = ({
  tooltipTitle = '',
  id,
  setSelectedRows = () => {},
  isStringArray = false,
  altIcon = false,
  getConversations = () => {}
}) => {
  const {
    allQueueOptions,
    allAgentOptions,
    queuesMap,
    agentsMap,
    fetch: fetchAgents
  } = useAgentsLoggedInQueues({ manual: true });

  const { response: allQueuesFromSystem } = useQueues({
    showDisabled: false,
    types: ['is_chat']
  });
  const { socketChatTS, emitEventTransferMultipleChatsToQueue } =
    useSocketConversationChatTs();
  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const { control, handleSubmit, errors, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      fila: null,
      user: null,
      transferToSelf: false
    }
  });

  const [isOpen, setIsOpen] = useState(false);
  const [transferMode, setTransferMode] = useState('QUEUE_FIRST');
  const [showOnlyWithAgents, setShowOnlyWithAgents] = useState(true);

  const currentUserLogin = useRef(localStorage.getItem('login'));
  const currentUserName = useRef(localStorage.getItem('name'));

  const watchedQueue = watch('fila');
  const watchedUser = watch('user');
  const watchedTransferToSelf = watch('transferToSelf');

  const filteredQueueOptions = useMemo(() => {
    let options;

    if (showOnlyWithAgents) {
      options = allQueueOptions;
    } else {
      options = allQueuesFromSystem
        .filter((queue) => queue.is_chat && !queue.disable)
        .map((queue) => ({
          label: queue.name,
          value: queue.id
        }));
    }

    if (watchedTransferToSelf) {
      const myLogin = currentUserLogin.current;
      const myQueues = agentsMap[myLogin]?.queueOptions || [];
      options = options.filter((opt) =>
        myQueues.find((q) => q.value === opt.value)
      );
    } else if (transferMode === 'USER_FIRST' && watchedUser?.value) {
      const userQueues = agentsMap[watchedUser.value]?.queueOptions || [];
      options = options.filter((opt) =>
        userQueues.find((q) => q.value === opt.value)
      );
    }

    return sortOptionsByLabel(options);
  }, [
    transferMode,
    watchedUser,
    watchedTransferToSelf,
    agentsMap,
    allQueueOptions,
    showOnlyWithAgents,
    queuesMap,
    allQueuesFromSystem
  ]);

  const filteredAgentOptions = useMemo(() => {
    let optionsToFilter = allAgentOptions;

    if (transferMode === 'QUEUE_FIRST' && watchedQueue?.value) {
      optionsToFilter = queuesMap[watchedQueue.value]?.agentOptions || [];
    }

    const validAgents = optionsToFilter.filter(
      (agent) => agent.value !== currentUserLogin.current
    );

    return sortOptionsByLabel(validAgents);
  }, [transferMode, watchedQueue, queuesMap, allAgentOptions, agentsMap]);

  useEffect(() => {
    if (isOpen) {
      fetchAgents();
      setTransferMode('QUEUE_FIRST');
      setShowOnlyWithAgents(true);
      setValue('fila', null);
      setValue('user', null);
      setValue('transferToSelf', false);
    }
  }, [isOpen, setValue, fetchAgents]);

  useEffect(() => {
    if (transferMode === 'QUEUE_FIRST') setValue('user', null);
  }, [watchedQueue?.value, transferMode, setValue]);

  useEffect(() => {
    if (transferMode === 'USER_FIRST') setValue('fila', null);
  }, [watchedUser?.value, transferMode, setValue]);

  useEffect(() => {
    if (watchedTransferToSelf) setValue('user', null);
  }, [watchedTransferToSelf, setValue]);

  const toggleModal = () => setIsOpen(!isOpen);

  const handleTransfer = (data) => {
    try {
      const chatIds = isStringArray
        ? id
        : id.map((item) => item['_id'] || item['id'] || item);

      const targetUserLogin = data.transferToSelf
        ? currentUserLogin.current
        : data.user?.value;

      const filaId = data.fila?.value ? Number(data.fila.value) : undefined;

      const payload = {
        chatIds: chatIds,
        socketAgent: socketChatTS?.id || null,
        destinationQueueId: filaId,
        destinationAgentLogin: targetUserLogin || undefined,
        responsibleForTransferred: {
          name: currentUserName.current,
          login: currentUserLogin.current
        }
      };

      emitEventTransferMultipleChatsToQueue(payload);

      setSelectedRows([]);
      setTimeout(() => {
        getConversations();
      }, 5000);

      setIsOpen(false);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao processar dados da transferência.');
    }
  };

  const existSelectedRows = id && id.length > 0;

  return (
    <>
      {altIcon
        ? verifyRoles(['CHAT_MULTIPLE_TRANSFER']) && (
            <TransferMultiplesChatsButtonAltIcon
              size="sm"
              color="link"
              title={tooltipTitle}
              onClick={toggleModal}
              disabled={!existSelectedRows}
              style={{
                minHeight: '46px',
                background: 'none',
                border: 'none',
                color: systemConfiguration.primary_color,
                paddingBottom: '6px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: existSelectedRows ? 'pointer' : 'not-allowed'
              }}
            >
              <SvgContainer
                color={systemConfiguration.primary_color}
                style={{
                  cursor: existSelectedRows ? 'pointer' : 'not-allowed'
                }}
              >
                <GrTransaction
                  style={{
                    stroke: 'blue',
                    cursor: existSelectedRows ? 'pointer' : 'not-allowed'
                  }}
                  size="20"
                  title="Transferir múltiplas conversas"
                />
              </SvgContainer>
            </TransferMultiplesChatsButtonAltIcon>
          )
        : verifyRoles(['CHAT_TRANSFERS']) && (
            <Button
              size="sm"
              color="primary"
              title={tooltipTitle}
              onClick={toggleModal}
              disabled={!existSelectedRows}
              style={{
                minHeight: '2rem',
                cursor: existSelectedRows ? 'pointer' : 'not-allowed'
              }}
            >
              Transferir várias
            </Button>
          )}

      <Modal
        className="modal-dialog-centered"
        isOpen={isOpen}
        toggle={toggleModal}
      >
        <div className="modal-header">
          <h6 className="modal-title" id="modal-title-default">
            Transferência em Lote
          </h6>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={toggleModal}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>

        <ModalBody>
          <div className="text-center mb-4">
            <h4 className="text-primary">
              <i className="fas fa-layer-group mr-2"></i>
              <span
                id="popoverSelectedChats"
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                {id?.length || 0} Conversas Selecionadas
              </span>
            </h4>

            <UncontrolledPopover
              trigger="legacy"
              placement="bottom"
              target="popoverSelectedChats"
            >
              <PopoverHeader>Conversas Selecionadas</PopoverHeader>
              <PopoverBody>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <ul style={{ paddingLeft: '20px', marginBottom: 0 }}>
                    {id?.map((item, index) => {
                      let label = '';
                      if (isStringArray) {
                        label = item;
                      } else {
                        label =
                          item.cliente?.nome ||
                          item.cliente?.telefone ||
                          item.id ||
                          item._id ||
                          'Desconhecido';
                      }
                      return (
                        <li key={index} style={{ fontSize: '0.85rem' }}>
                          {label}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </PopoverBody>
            </UncontrolledPopover>

            <small className="text-muted">
              Configure o destino para todas as conversas selecionadas.
            </small>
          </div>

          <hr style={{ marginTop: '-10px' }} />

          <Form
            className="needs-validation"
            onSubmit={handleSubmit(handleTransfer)}
          >
            <Row className="mb-3">
              <Col md="12">
                <Label style={{ fontWeight: 'bold' }}>Modo de seleção:</Label>
                <div className="d-flex align-items-center">
                  <FormGroup check inline>
                    <Label check style={{ cursor: 'pointer' }}>
                      <Input
                        type="radio"
                        name="transferMode"
                        checked={transferMode === 'QUEUE_FIRST'}
                        onChange={() => {
                          setTransferMode('QUEUE_FIRST');
                          setValue('user', null);
                        }}
                      />{' '}
                      Fila primeiro
                    </Label>
                  </FormGroup>
                  <FormGroup check inline>
                    <Label check style={{ cursor: 'pointer' }}>
                      <Input
                        type="radio"
                        name="transferMode"
                        checked={transferMode === 'USER_FIRST'}
                        onChange={() => {
                          setTransferMode('USER_FIRST');
                          setValue('fila', null);
                        }}
                      />{' '}
                      Atendente primeiro
                    </Label>
                  </FormGroup>
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md="12">
                <FormGroup check>
                  <Label
                    check
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Controller
                      name="transferToSelf"
                      control={control}
                      defaultValue={false}
                      render={(props) => {
                        const { onChange, onBlur, value, ref } =
                          props.field || props;
                        return (
                          <Input
                            type="checkbox"
                            onChange={(e) => onChange(e.target.checked)}
                            onBlur={onBlur}
                            checked={value}
                            innerRef={ref}
                          />
                        );
                      }}
                    />{' '}
                    Transferir para mim mesmo
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            {transferMode === 'QUEUE_FIRST' && (
              <Row className="mb-3">
                <Col md="12">
                  <FormGroup check>
                    <Label
                      check
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Oculta filas que não possuem nenhum agente logado no momento"
                    >
                      <Input
                        type="checkbox"
                        checked={showOnlyWithAgents}
                        onChange={(e) =>
                          setShowOnlyWithAgents(e.target.checked)
                        }
                      />{' '}
                      Mostrar apenas filas com atendentes logados
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            )}

            <Row>
              <Col md="12">
                {transferMode === 'QUEUE_FIRST' ? (
                  <>
                    <div className="mb-3">
                      <FlexControllerV2
                        type="select"
                        name="fila"
                        control={control}
                        label="Fila"
                        options={filteredQueueOptions}
                        isClearable={false}
                        isSearchable={true}
                        rules={{ required: 'A fila é obrigatória' }}
                        errors={errors}
                        placeholder="Selecione a fila"
                      />
                    </div>

                    {watchedQueue && !watchedTransferToSelf && (
                      <div className="mb-3">
                        <FlexControllerV2
                          type="select"
                          name="user"
                          control={control}
                          label="Atendente"
                          options={filteredAgentOptions}
                          isClearable={true}
                          isSearchable={true}
                          errors={errors}
                          placeholder="Selecione o atendente"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {!watchedTransferToSelf && (
                      <div className="mb-3">
                        <FlexControllerV2
                          type="select"
                          name="user"
                          control={control}
                          label="Atendente"
                          options={filteredAgentOptions}
                          isClearable={true}
                          isSearchable={true}
                          errors={errors}
                          placeholder="Selecione o atendente"
                        />
                      </div>
                    )}

                    {(watchedUser || watchedTransferToSelf) && (
                      <div className="mb-3">
                        <FlexControllerV2
                          type="select"
                          name="fila"
                          control={control}
                          label="Fila"
                          options={filteredQueueOptions}
                          isClearable={false}
                          isSearchable={true}
                          rules={{ required: 'A fila é obrigatória' }}
                          errors={errors}
                          placeholder="Selecione a fila"
                        />
                      </div>
                    )}
                  </>
                )}
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-3">
              <Button
                color="danger"
                data-dismiss="modal"
                type="button"
                onClick={toggleModal}
                style={{
                  ...buttonStyle(systemConfiguration.cancelation_button_color),
                  marginRight: '10px'
                }}
              >
                Fechar
              </Button>

              <Button
                color="primary"
                type="submit"
                style={buttonStyle(
                  systemConfiguration.confirmation_button_color
                )}
              >
                {watchedTransferToSelf ? 'Transferir para mim' : 'Transferir'}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default TransferManyButton;
